import React, { useState, useContext, useCallback } from "react";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { Row, Col } from "antd";
import { getDateFormat } from "/app/src/helpers/time";
import { Form, Select } from "formik-antd";
import Verb from "./verb";
import Value from "./value";
import TimeZoneContext from "/app/src/contexts/TimeZoneContext";
import { useTranslation } from "react-i18next";
import { Report, ReportColumn, ReportFilter } from "/app/src/models";
import { SearchFilter } from "/app/src/components/generic/components/searchFilter";
import { convertArrayToCSV } from "/app/src/helpers";
import FormikDisabler from "/app/src/components/generic/components/formikDisabler";
import DisabledSubmitButton from "/app/src/components/generic/components/buttons/DisabledSubmitButton";
import { useAccessContext } from "/app/src/contexts/hooks/useAccessContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reportFilterService } from "/app/src/services";
import { createReportFilterSchema } from "/app/src/schemas/reports/reportSchema";
import { handlePromiseError } from "/app/src/helpers/api";

/**
 * Formats the form values based on the provided date range and timezone.
 *
 * @param {FormValues} values - The form values containing the value, date range, and report details.
 * @param {string} timeZone - The timezone used for formatting the date.
 * @returns {Object} - An object containing the report column ID, formatted value, verb, date range, and report ID.
 */
function formatForm(values: FormValues, timeZone: string) {
  let formattedValue = values.value;
  // If value is a list, convert to comma separated string
  formattedValue = convertArrayToCSV(formattedValue);
  if (values.dateRange === "specificTime") {
    formattedValue = getDateFormat(
      formattedValue,
      "YYYY-MM-DDTHH:mm:ss",
      timeZone,
    );
  }
  return {
    reportColumnId: values.reportColumnId,
    value: formattedValue,
    verb: values.verb,
    dateRange: values.dateRange,
    reportId: values.reportId,
  };
}

interface FormValues {
  reportColumnId?: number;
  verb?: string;
  reportId?: number;
  value: string | string[];
  dateRange: string;
}
/**
 * Creates a new report filter component.
 *
 * @param {Object} param0 - An object containing the properties for the filter.
 * @param {Report} param0.report - The report object.
 * @param {ReportColumn[]} param0.reportColumns - An array of report column objects.
 * @returns {JSX.Element} The rendered new report filter component.
 */
export default function NewReportFilter({
  report,
  reportColumns,
}: {
  report: Report;
  reportColumns: ReportColumn[];
}) {
  const { t } = useTranslation();
  const [filterType, setFilterType] = useState("");
  const { timeZone } = useContext(TimeZoneContext);
  const { canEdit } = useAccessContext();
  const queryClient = useQueryClient();

  const { mutateAsync: addFilter } = useMutation({
    mutationFn: (values: FormValues) => {
      return reportFilterService
        .createSingle(formatForm(values, timeZone))
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["reportFilters", report.id],
        (oldData: { report_filters: ReportFilter[] }) => {
          return {
            report_filters: [...oldData.report_filters, data.report_filter],
          };
        },
      );
    },
  });

  const addFilterHandler = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      values.reportId = report.id;
      await addFilter(formatForm(values, timeZone)).finally(() => {
        actions.resetForm();
      });
    },
    [addFilter, report.id, timeZone],
  );

  const mapColumns = useCallback((c: ReportColumn) => {
    return (
      <Select.Option key={c.id} value={c.id}>
        {c.name}
      </Select.Option>
    );
  }, []);
  // Set filter type based on column selected
  const onColumnChange = useCallback(
    (
      value: number,
      {
        setFieldValue,
      }: { setFieldValue: (field: string, value: string) => void },
    ) => {
      const col = reportColumns.find((obj) => obj.id === value);
      if (col?.filterType) {
        const newFilterType =
          col.filterType === "DateTime" && col.type === "comparison"
            ? "Number"
            : col.filterType;
        setFilterType(newFilterType);
      } else {
        setFilterType("");
      }
      setFieldValue("verb", "");
      setFieldValue("dateRange", "");
      setFieldValue("value", "");
    },
    [reportColumns, setFilterType],
  );

  const newFilterForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isValid, isSubmitting, values, setFieldValue }) => {
        const columnChangeHandler = (
          () => (value: number) =>
            onColumnChange(value, { setFieldValue })
        )();

        return (
          <Form>
            <FormikDisabler disabled={!canEdit} />
            <Row justify="start" gutter={16}>
              <Col span={5}>
                <Form.Item name="reportColumnId">
                  <SearchFilter
                    name="reportColumnId"
                    placeholder={t("translation:select_column")}
                    mapOptionsFn={mapColumns}
                    list={reportColumns}
                    sort
                    onChange={columnChangeHandler}
                  />
                </Form.Item>
              </Col>
              <Verb type={filterType} />

              <Value type={filterType} verb={values.verb} />
              <Col span={3} offset={1}>
                <DisabledSubmitButton
                  type="primary"
                  size="large"
                  block
                  disabled={!dirty || isSubmitting || !isValid}
                >
                  {t("translation:add")}
                </DisabledSubmitButton>
              </Col>
            </Row>
          </Form>
        );
      },
      [canEdit, t, mapColumns, reportColumns, filterType, onColumnChange],
    );
  return (
    <div className="newFilter">
      <Formik
        component={newFilterForm}
        initialValues={{
          verb: "",
          value: "",
          dateRange: "",
        }}
        validationSchema={createReportFilterSchema(reportColumns)}
        onSubmit={addFilterHandler}
      />
    </div>
  );
}
